import { environment } from '../../environment';
import { DeckEvent, PublicDeck } from '../../components/decks/render';

// The hosted deck's public API: no login, no auth headers (the share token in the path is the key).
const base = (): string => `${environment.v2Url}/public/decks`;
const path = (token: string, rest = ''): string => `${base()}/${encodeURIComponent(token)}${rest}`;

export type DeckLoad =
    | { ok: true; deck: PublicDeck }
    | { ok: false; status: number; message: string; rep_name: string | null };

// #region fetchPublicDeck
export async function fetchPublicDeck(token: string): Promise<DeckLoad> {
    try {
        const res = await fetch(path(token), { method: 'GET', headers: { Accept: 'application/json' }, credentials: 'omit' });
        const body = await res.json().catch(() => null);
        if (res.ok && body && body.doc) return { ok: true, deck: body as PublicDeck };
        // 410 bodies are { message }; a rep name is used if the server ever adds one.
        const rep = body?.rep_name ?? body?.rep?.name ?? null;
        return { ok: false, status: res.status, message: typeof body?.message === 'string' ? body.message : '', rep_name: typeof rep === 'string' ? rep : null };
    } catch {
        return { ok: false, status: 0, message: 'We couldn’t reach the server. Check your connection and try again.', rep_name: null };
    }
}

// #region fetchHouseTheme
/** The live Wallmates brand (the brand kit marketing emails use), for the sample deck. Null if it can't be had. */
export async function fetchHouseTheme(): Promise<PublicDeck['context']['theme'] | null> {
    try {
        const res = await fetch(`${base()}/brand`, { headers: { Accept: 'application/json' }, credentials: 'omit' });
        const body = await res.json().catch(() => null);
        return res.ok && body && typeof body.accent === 'string' ? body : null;
    } catch {
        return null;
    }
}

// #region postDeckEvent
/** Fire-and-forget: tracking never gets in the lead's way. */
export function postDeckEvent(token: string, event: DeckEvent): void {
    try {
        void fetch(path(token, '/events'), {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(event), keepalive: true, credentials: 'omit'
        }).catch(() => undefined);
    } catch { /* ignore */ }
}

// #region beaconDeckEvent
/**
 * For pagehide, when a normal request may be cancelled. sendBeacon can't send application/json cross-origin
 * without a preflight (Chrome refuses it), so the JSON goes as text/plain; the events route must parse both.
 */
export function beaconDeckEvent(token: string, event: DeckEvent): void {
    const body = JSON.stringify(event);
    try {
        if (navigator.sendBeacon && navigator.sendBeacon(path(token, '/events'), new Blob([body], { type: 'text/plain;charset=UTF-8' }))) return;
    } catch { /* fall through */ }
    postDeckEvent(token, event);
}

// #region deckPdfUrl
export const deckPdfUrl = (token: string): string => path(token, '/pdf');
